import { useQueryClient } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';
import LangEnum from '../../../../../../lang/LangEnum';
import useOrderBookStore from '../../../../../../stores/OrderBook/orderBook.store';
import useSharedEntitystore from '../../../../../../stores/SharedEntities/sharedEntities.store';
import snackbarStore from '../../../../../../stores/Snackbar.store';
import { useApplicationContext } from '../../../../../context/ApplicationContext/ApplicationContext';
import { useLoadingStatusStore } from '../../../../../context/loadingStatus/LoadingStatus.store';
import { UseProductGridOutput } from '../../../../../orders/products/interfaces/use-product-grid.interface';
import { useGridStoreApi } from '../../../../../orders/products/product/grid/store/GridStoreProvider';
import { produceItemsToStore } from '../../../../../orders/products/product/grid/store/produceItemsToStore';
import { useCombinationButtonStatus } from '../../../../../orders/products/product/grid/store/useCombinationButtonStatus';
import { ordersRepository } from '../../../../../orders/repositories/ordersRepository/orders.repository';
import { invalidateAffectedOrderProductsGridQueries } from '../../../../../orders/utils/orderCalculate.utils';
import { AvailabilityEnum } from '../../../../../products/ProductTypes';
import {
  CombinationButtonStatusEnum,
  IGridStatus,
} from '../../../../../products/products-details-v2/hooks/useCombination';
import { isItemInOrderStatus } from '../../../../../products/products-details-v2/hooks/useCombination.utils';
import { useTranslation } from '../../../../../translation/TranslationContext';
import {
  buildRemovedCombinationTargets,
  resolveBlockedForCustomerTooltip,
} from './catalogListAddOrderButton.utils';

interface CatalogListAddOrderButtonProduct {
  productId: string;
  productColorId: string;
}

interface CatalogListAddOrderButtonCombination {
  combinationKey: string;
  availability: AvailabilityEnum;
  deliveryWindowId?: string | null;
}

interface CatalogListAddOrderButtonSurface {
  isPublic: boolean;
  canEditOrders: boolean;
  canAddToOrder: boolean;
}

interface CatalogListAddOrderButtonStatus {
  value: IGridStatus;
  onChange: (status: IGridStatus) => void;
}

export interface CatalogListAddOrderButtonProps {
  product: CatalogListAddOrderButtonProduct;
  orderId?: string;
  combination: CatalogListAddOrderButtonCombination;
  productGrid: UseProductGridOutput;
  surface: CatalogListAddOrderButtonSurface;
  status: CatalogListAddOrderButtonStatus;
  onNoSelectedOrder: () => void;
}

export const useCatalogListAddOrderButton = ({
  product,
  orderId,
  combination,
  productGrid,
  surface,
  status,
  onNoSelectedOrder,
}: CatalogListAddOrderButtonProps) => {
  const { translate } = useTranslation();
  const { team } = useApplicationContext();
  const queryClient = useQueryClient();
  const gridStoreApi = useGridStoreApi();
  const openLoginModal = useSharedEntitystore((state) => state.handleChangeIsOpenLoginModal);
  const handleLoadingStatus = useLoadingStatusStore((state) => state.handleLoadingStatus);
  const { selectedOrderId, reloadOrderItemsQuantity } = useOrderBookStore(
    useShallow((state) => ({
      selectedOrderId: state.selectedOrder?.id,
      reloadOrderItemsQuantity: state.reloadOrderItemsQuantity,
    })),
  );

  const { productId, productColorId } = product;
  const { grid } = productGrid;
  const hasOrderItem = grid.some((gridItem) => !!gridItem.orderItemId);
  const hasKitOrderItem = grid.some((gridItem) => !!gridItem.orderItemKitId);

  const setGridButtonStatus = status.onChange;

  const renderedStatus = useCombinationButtonStatus({
    combinationKey: combination.combinationKey,
    hasEmptyAvailability: false,
    fallback: status.value,
  });
  const isInOrder = isItemInOrderStatus(renderedStatus);

  const onAdd = () => {
    if (surface.isPublic) {
      openLoginModal(true);
      return;
    }

    if (!selectedOrderId || !orderId) {
      onNoSelectedOrder();
      return;
    }

    const [firstGridItem] = grid;

    if (!firstGridItem) {
      return;
    }

    produceItemsToStore({
      api: gridStoreApi,
      combinationKey: combination.combinationKey,
      items: [
        {
          orderId,
          productColorId,
          skuId: firstGridItem.skuId,
          quantity: 0,
          availability: combination.availability,
          deliveryWindowId: combination.deliveryWindowId,
        },
      ],
    });
  };

  const onRemove = async () => {
    if (surface.isPublic) {
      return;
    }

    if (!selectedOrderId || !orderId) {
      onNoSelectedOrder();
      return;
    }

    const { removedOrderProducts, combinationKeys } = buildRemovedCombinationTargets({
      productColorId,
      orderId,
      availability: combination.availability,
      deliveryWindowId: combination.deliveryWindowId,
    });

    setGridButtonStatus({ itemQuantity: 0, type: CombinationButtonStatusEnum.LOADING });

    try {
      await handleLoadingStatus({
        requestFn: () =>
          ordersRepository(orderId).deleteOrderProduct({
            productId,
            productColorId,
            availabilities: removedOrderProducts.map((removed) => removed.availability),
            deliveryWindowId: combination.deliveryWindowId ?? undefined,
          }),
        onSuccess: async () => {
          const combinationKeysToClear = new Set([combination.combinationKey, ...combinationKeys]);

          combinationKeysToClear.forEach((combinationKey) => {
            gridStoreApi.getState().clearCombination(combinationKey);
          });
          setGridButtonStatus({ itemQuantity: 0, type: CombinationButtonStatusEnum.ADD });

          try {
            const affectedOrderProducts = await reloadOrderItemsQuantity(
              true,
              orderId,
              orderId === selectedOrderId,
            );

            invalidateAffectedOrderProductsGridQueries({
              orderId,
              queryClient,
              teamId: team?.id,
              isOfflineMode: false,
              affectedOrderProducts: [...removedOrderProducts, ...(affectedOrderProducts ?? [])],
            });
          } catch {
            return;
          }
        },
        onFailure: ({ code, message }) => {
          setGridButtonStatus({ itemQuantity: 0, type: CombinationButtonStatusEnum.ADDED });
          snackbarStore.handleMessage({
            type: 'error',
            message:
              (code && translate(code, { label: message })) ||
              translate(LangEnum.SNACKBAR_ERROR_REMOVING),
          });
        },
      });
    } catch {
      setGridButtonStatus({ itemQuantity: 0, type: CombinationButtonStatusEnum.ADDED });
    }
  };

  const isBlockedForCustomer = !surface.canAddToOrder && !hasOrderItem;
  const isPublicFunnel = surface.isPublic && !isInOrder;

  const isDisabled =
    !isPublicFunnel &&
    (!surface.canEditOrders || isBlockedForCustomer || hasKitOrderItem || grid.length === 0);

  return {
    isDisabled,
    gridButtonStatus: status.value,
    tooltipMessage: resolveBlockedForCustomerTooltip({
      isPublic: surface.isPublic,
      isBlockedForCustomer,
    }),
    onAdd,
    onRemove,
  };
};
